import type { ComponentType } from 'react';
import {
  Activity,
  BarChart3,
  Calendar,
  CalendarClock,
  ClipboardList,
  Coins,
  FileText,
  GitCompareArrows,
  LayoutGrid,
  Users2,
  Zap,
} from 'lucide-react';

/**
 * Canonical left-menu structure for the `/insights` workspace (EL-216).
 *
 * This module is the SINGLE SOURCE OF TRUTH for the Insights sidebar. It
 * declares the FULL target information architecture — including feature areas
 * that do not exist in the product yet. Those are flagged `notImplemented` and
 * are **hidden from the live menu** (not rendered at all); a section in which
 * every item is `notImplemented` is dropped entirely. They live here so that
 * the day a feature ships you add its <Route> and delete the flag, and the
 * item "auto-moves into the correct view" — appearing in its correct section
 * and position with no further menu surgery.
 *
 * See ./CLAUDE.md for the full activation recipe and editing rules. Keep this
 * file purely declarative (data only) — runtime state (survey gating, feature
 * flags) is resolved by AppSidebar.tsx, NOT here.
 */

/**
 * Runtime gate for an item. The item renders as a dimmed, non-clickable entry
 * until the gate opens. Resolved in AppSidebar against the live InsightsNav
 * signals. (Distinct from `notImplemented`, which hides the item entirely.)
 *
 * - `always`          — always interactive (default when `gate` is omitted).
 * - `completedSurvey` — unlocks once the tenant has at least one completed
 *                       survey (`hasCompletedSurvey`).
 * - `activeSurvey`    — unlocks once the tenant has at least one active
 *                       survey (`hasActiveSurvey`).
 * - `completedOrLaunchingSurvey` — unlocks once the tenant has a completed
 *                       survey OR one that's scheduled/in-progress
 *                       (`hasCompletedSurvey || hasScheduledOrLiveSurvey`).
 *                       Used by Overview (EL-788) so a tenant who just
 *                       launched a survey has a visible path to the
 *                       post-launch banner before anything has completed.
 */
export type NavGate =
  | 'always'
  | 'completedSurvey'
  | 'activeSurvey'
  | 'completedOrLaunchingSurvey'
  /**
   * The tenant's surveys carry BOTH driver- and KPI-typed modules. Segments
   * need both: drivers are what people are clustered on, and a KPI is what
   * segments are ranked and statused against. Engie's surveys are entirely
   * KPI-typed Human Energy Scans with no drivers, so segmentation is not
   * merely empty for them — it is impossible.
   */
  | 'segmentable';

export interface InsightsNavItemDef {
  /** i18n key under the `assist:sidebar` namespace (dot-separated, nested). */
  titleKey: string;
  /**
   * Route this item links to. For `notImplemented` items this is the path the
   * feature WILL live at once built — declaring it now is what lets the item
   * snap into place automatically the moment its <Route> is wired up.
   */
  url: string;
  icon: ComponentType<{ className?: string }>;
  /** Runtime gate. Defaults to `always`. */
  gate?: NavGate;
  /**
   * Remove the item entirely when its gate is closed, instead of dimming it.
   *
   * For a gate that a tenant can never open by acting — no amount of launching
   * surveys gives Engie driver questions — a permanently dimmed entry is just
   * a dead control advertising a feature they cannot reach. Gates that a
   * tenant CAN open (launch a survey, wait for it to close) stay dimmed,
   * because there the item is a signpost.
   */
  hideWhenGated?: boolean;
  /**
   * The feature/page behind this item does not exist yet (or is only a
   * "coming soon" stub). The item is HIDDEN from the live menu — not rendered.
   * To activate it: build the real page, add its <Route> in
   * InsightsDashboard.tsx, then delete this flag (see ./CLAUDE.md).
   */
  notImplemented?: boolean;
}

export interface InsightsNavSectionDef {
  /** i18n key for the section header under `assist:sidebar.section`. */
  labelKey: string;
  items: InsightsNavItemDef[];
}

/**
 * NOTE: The "Get started / Onboarding" group from the mockup is rendered
 * separately in AppSidebar (it is conditional on the tenant's onboarding
 * state), so it is intentionally NOT listed here.
 */
export const INSIGHTS_NAV_SECTIONS: InsightsNavSectionDef[] = [
  {
    labelKey: 'sidebar.section.insightsAndActions',
    items: [
      { titleKey: 'sidebar.overview', url: '/insights', icon: LayoutGrid, gate: 'completedOrLaunchingSurvey' },
      { titleKey: 'sidebar.teams', url: '/insights/teams', icon: Users2, gate: 'completedSurvey' },
      { titleKey: 'sidebar.drivers', url: '/insights/modules', icon: Activity, gate: 'completedSurvey' },
      { titleKey: 'sidebar.correlations', url: '/insights/correlations', icon: GitCompareArrows, gate: 'completedSurvey' },
      {
        titleKey: 'sidebar.segments',
        url: '/insights/segments',
        icon: Users2,
        gate: 'segmentable',
        hideWhenGated: true,
      },
      { titleKey: 'sidebar.actions', url: '/insights/actions', icon: Zap, gate: 'completedSurvey' },
    ],
  },
  {
    // Absenteeism is a whole feature area that is not built yet, so the entire
    // section is hidden today. As each page ships, add its <Route> and remove
    // the matching `notImplemented` flag — the item (and the section header)
    // appears in place.
    labelKey: 'sidebar.section.absenteeism',
    items: [
      { titleKey: 'sidebar.absenteeism.overview', url: '/insights/absenteeism', icon: Calendar, notImplemented: true },
      { titleKey: 'sidebar.absenteeism.teams', url: '/insights/absenteeism/teams', icon: Users2, notImplemented: true },
      { titleKey: 'sidebar.absenteeism.longTermAbsence', url: '/insights/absenteeism/long-term', icon: CalendarClock, notImplemented: true },
      { titleKey: 'sidebar.absenteeism.costs', url: '/insights/absenteeism/costs', icon: Coins, notImplemented: true },
      { titleKey: 'sidebar.absenteeism.benchmarks', url: '/insights/absenteeism/benchmarks', icon: BarChart3, notImplemented: true },
      { titleKey: 'sidebar.absenteeism.individualCases', url: '/insights/absenteeism/cases', icon: FileText, notImplemented: true },
    ],
  },
  {
    labelKey: 'sidebar.section.surveys',
    items: [
      { titleKey: 'sidebar.surveysOverview', url: '/insights/surveys', icon: ClipboardList },
      { titleKey: 'sidebar.participation', url: '/insights/participation', icon: BarChart3, gate: 'activeSurvey' },
    ],
  },
  // NOTE: The "Account" section (My Profile, Security, Data, Audit Logs) used to
  // live here. It now lives in the user-profile dropdown in the top-right header
  // (UserProfileDropdown.tsx) instead of the sidebar.
];
